/**
 * The server resolver the tracker's last recovery step asks, as an interface
 * so tests can script its answers, with the wire shapes it speaks.
 *
 * The request carries what the add-in already knows: the live document, the
 * anchor from `AnchorMemory`, the text the redline produces, and the
 * paragraphs the user changed recently as hints. The core runs the shared
 * `ParagraphLocator` through the web app and, when that is not enough, its LLM
 * locate. The response keeps the core's enums as enums (`ResolutionPath`,
 * `RedlinePlacement`) so nothing is flattened to a string on the way in.
 */
import { z } from "zod";

import { resolutionPathSchema, type SpanAnchor } from "@common/utils/redline/paragraph-locator";
import { redlinePlacementSchema } from "@common/utils/redline/wire/redline-placement";

export const llmCandidateSchema = z.object({
    paragraphIndex: z.number().int(),
    relation: z.enum(["is_the_paragraph", "belongs_after", "belongs_before", "is_inside"]),
    anchorPhrase: z.string().nullable(),
    confidence: z.number(),
    reason: z.string(),
    chunks: z.array(z.number().int()),
});
export type LlmCandidate = z.infer<typeof llmCandidateSchema>;

export const reanchorResponseSchema = z.object({
    anchorState: z.enum(["relocated", "conflicted", "unresolved"]),
    paragraphIndexes: z.array(z.number().int()),
    placement: redlinePlacementSchema.nullable(),
    resolutionPath: resolutionPathSchema.nullable(),
    resolverExplanation: z.string().nullable(),
    llmCandidates: z.array(llmCandidateSchema),
});
export type ReanchorResponse = z.infer<typeof reanchorResponseSchema>;

export interface ReanchorRequest {
    documentFlatOpc: string;
    anchor: SpanAnchor;
    redlineDesiredText: string;
    changedParagraphTexts: string[];
}

export interface ReanchorClient {
    /** Null when the add-in has no session or the server did not answer; the step treats both as not found. */
    resolve(request: ReanchorRequest): Promise<ReanchorResponse | null>;
}
